/**
 * NativeMarkdown — renders markdown text in React Native chat bubbles.
 *
 * Extracted from GoalDetail.native.tsx so Cedar chat messages show formatted
 * output (bold, italic, bullets, headings, etc.) instead of raw markdown text.
 *
 * Usage:
 *   <NativeMarkdown text={message.text} textStyle={styles.cedarText} />
 *
 * `textStyle` is applied to paragraph / plain-text nodes so the caller's
 * font-size, color and line-height are preserved.  Heading / bullet styles
 * intentionally override the base because structural elements need their own
 * sizing.
 *
 * A HEADING TAKES THE CALLER'S INK, and only that. It used to take nothing from
 * `textStyle`, so it had no colour at all and React Native drew it in the
 * platform's default: black on an iPhone (invisible on a dark bubble), and on an
 * Android phone set to dark a light grey — on Cedar's white bubble, because the
 * app's Android theme is DayNight. Its size, weight and spacing stay its own.
 */

import React from 'react';
import { StyleSheet, Text, View, type TextStyle, type ViewStyle } from 'react-native';
import { makeStyles, themed } from '../theme';

type Sheet = ReturnType<typeof useS>;

/** The caller's ink, and nothing else of its style. */
function inkOf(textStyle?: TextStyle | TextStyle[]): TextStyle | null {
  const color = StyleSheet.flatten(textStyle)?.color;
  return color === undefined ? null : { color };
}

// ---------------------------------------------------------------------------
// Inline parser — bold + italic
// ---------------------------------------------------------------------------
function renderInline(text: string, s: Sheet): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_)/g);
  if (parts.length === 1) return text;

  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
          return <Text key={i} style={s.bold}>{part.slice(2, -2)}</Text>;
        }
        if (
          (part.startsWith('*') && part.endsWith('*') && part.length > 2) ||
          (part.startsWith('_') && part.endsWith('_') && part.length > 2)
        ) {
          return <Text key={i} style={s.italic}>{part.slice(1, -1)}</Text>;
        }
        return part;
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// Block parser
// ---------------------------------------------------------------------------
function renderMarkdown(
  text: string,
  s: Sheet,
  textStyle?: TextStyle | TextStyle[],
  selectable?: boolean,
): React.ReactNode {
  const lines = text.split('\n');
  const nodes: React.ReactNode[] = [];
  const ink = inkOf(textStyle);
  let key = 0;

  for (const line of lines) {
    if (line.startsWith('# ')) {
      nodes.push(
        <Text key={key++} style={[ink, s.h1]} selectable={selectable}>{line.slice(2).trim()}</Text>,
      );
    } else if (line.startsWith('## ')) {
      nodes.push(
        <Text key={key++} style={[ink, s.h2]} selectable={selectable}>{line.slice(3).trim()}</Text>,
      );
    } else if (line.startsWith('### ')) {
      nodes.push(
        <Text key={key++} style={[ink, s.h3]} selectable={selectable}>{line.slice(4).trim()}</Text>,
      );
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      nodes.push(
        <View key={key++} style={s.bulletRow}>
          <Text style={[s.bulletDot, textStyle]} selectable={selectable}>•</Text>
          <Text style={[s.bulletText, textStyle]} selectable={selectable}>{renderInline(line.slice(2).trim(), s)}</Text>
        </View>,
      );
    } else if (/^\d+\.\s/.test(line)) {
      const match = line.match(/^(\d+)\.\s(.*)/);
      if (match) {
        nodes.push(
          <View key={key++} style={s.bulletRow}>
            <Text style={[s.bulletDot, textStyle]} selectable={selectable}>{match[1]}.</Text>
            <Text style={[s.bulletText, textStyle]} selectable={selectable}>{renderInline(match[2].trim(), s)}</Text>
          </View>,
        );
      }
    } else if (/^---+$/.test(line.trim()) || /^\*\*\*+$/.test(line.trim())) {
      nodes.push(<View key={key++} style={s.hr} />);
    } else if (line.trim() === '') {
      nodes.push(<View key={key++} style={s.spacer} />);
    } else {
      nodes.push(
        <Text key={key++} style={[s.paragraph, textStyle]} selectable={selectable}>{renderInline(line, s)}</Text>,
      );
    }
  }

  return <>{nodes}</>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
interface NativeMarkdownProps {
  text: string;
  /** Styles forwarded to paragraph / plain-text nodes (preserves caller colours, size, etc.) */
  textStyle?: TextStyle | TextStyle[];
  /** Optional wrapper style */
  containerStyle?: ViewStyle;
  /** Allow long-press text selection on all text nodes */
  selectable?: boolean;
}

const NativeMarkdown: React.FC<NativeMarkdownProps> = ({
  text,
  textStyle,
  containerStyle,
  selectable,
}) => {
  const s = useS();
  if (!text || text.trim().length === 0) return null;
  return <View style={containerStyle}>{renderMarkdown(text, s, textStyle, selectable)}</View>;
};

export default NativeMarkdown;

// ---------------------------------------------------------------------------
// Styles (heading / structural — callers provide their own paragraph styles
// via textStyle prop)
// ---------------------------------------------------------------------------
const useS = makeStyles((t) => ({
  h1: {
    fontSize: 20,
    fontWeight: '700',
    marginTop: 10,
    marginBottom: 4,
  },
  h2: {
    fontSize: 17,
    fontWeight: '700',
    marginTop: 8,
    marginBottom: 3,
  },
  h3: {
    fontSize: 15,
    fontWeight: '600',
    marginTop: 6,
    marginBottom: 2,
  },
  paragraph: {
    // Fallback — overridden by textStyle in practice
    fontSize: 15,
    lineHeight: 22,
    marginBottom: 3,
  },
  bold: {
    fontWeight: '700',
  },
  italic: {
    fontStyle: 'italic',
  },
  bulletRow: {
    flexDirection: 'row',
    marginBottom: 3,
    paddingRight: 4,
  },
  bulletDot: {
    fontSize: 15,
    lineHeight: 22,
    marginRight: 6,
  },
  bulletText: {
    flex: 1,
    fontSize: 15,
    lineHeight: 22,
  },
  hr: {
    height: 1,
    backgroundColor: themed(t, '#e0e0e0', t.line.border),
    marginVertical: 6,
  },
  spacer: {
    height: 4,
  },
}));
